import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { finalize } from 'rxjs';
import {
  ChevronDownIconComponent,
  CloseIconComponent,
  InfoIconComponent,
} from '@kuiper/icons';
import { OverlayLoaderComponent } from '../../../../../common/components/overlay-loader/overlay-loader.component';
import { SectionHeaderComponent } from '../../../../../common/components/section-header/section-header.component';
import { ActionListComponent } from '../../../../../common/components/action-list/action-list.component';
import { ActionList } from '../../../../../common/models/action-list.model';
import { EMPLOYEE_ROUTES } from '../../../../../common/constants/employee.routing.constants';
import { TenantService } from '../../../../../common/services/tenant/tenant.service';
import { CookieService } from '../../../../../common/services/cookie/cookie.service';
import { AppStorage } from '../../../../../common/enums/app-storage';
import { AvatarComponent } from '../../../../../../ui-components/lib/avatar/avatar.component';
import { BadgeComponent } from '../../../../../../ui-components/lib/badge/badge.component';
import { ExpandCollapseAllComponent } from '../../../../../../ui-components/lib/expand-collapse-all/expand-collapse-all.component';
import { SanitizeHtmlPipe } from '../../../../../../ui-components/pipes/sanitize-html/sanitize-html.pipe';
import { TippyTooltipDirective } from '../../../../../../ui-components/lib/tooltip/tippy-tooltip.directive';
import { DelegationDetails } from '../delegation.model';
import {
  DELEGATION_EDIT_ACTION,
  DELEGATION_EDITABLE_STATUSES,
  DELEGATION_STATUS_BADGE_CLASS,
} from '../delegation.constant';
import { DelegationService } from '../delegation.service';
import { DELEGATION_TASK_SCOPE_OPTIONS } from '../delegation-form/delegation-form.constant';

@Component({
  selector: 'lib-delegation-details',
  imports: [
    DatePipe,
    TranslocoPipe,
    ChevronDownIconComponent,
    InfoIconComponent,
    OverlayLoaderComponent,
    SectionHeaderComponent,
    ActionListComponent,
    AvatarComponent,
    BadgeComponent,
    ExpandCollapseAllComponent,
    SanitizeHtmlPipe,
    TippyTooltipDirective,
    CloseIconComponent,
  ],
  templateUrl: './delegation-details.component.html',
  styleUrl: './delegation-details.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class DelegationDetailsComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly transloco = inject(TranslocoService);
  private readonly tenantService = inject(TenantService);
  private readonly cookieService = inject(CookieService);
  private readonly delegationService = inject(DelegationService);

  readonly isLoading = signal(true);
  readonly isAllSectionsExpanded = signal(true);
  readonly details = signal<DelegationDetails | null>(null);

  readonly statusBadgeClass = computed(() => {
    const status = this.details()?.state?.value;
    return status ? (DELEGATION_STATUS_BADGE_CLASS[status] ?? '') : '';
  });

  readonly reasonText = computed(() => {
    const reason = this.details()?.reason;
    if (!reason) {
      return '';
    }

    return reason;
  });

  readonly taskScopeLabel = computed(() => {
    const taskScope = this.details()?.task_scope;
    return taskScope
      ? taskScope.toString()
      : this.transloco.translate(DELEGATION_TASK_SCOPE_OPTIONS[0].label);
  });

  readonly actionMenuItems = computed<ActionList[]>(() => {
    const details = this.details();
    const status = details?.state.value;
    const isLoggedInDelegatee =
      String(details?.delegate?.id ?? '') === this.loggedInUserId;

    if (
      !status ||
      !DELEGATION_EDITABLE_STATUSES.includes(status) ||
      isLoggedInDelegatee
    ) {
      return [];
    }

    return [
      {
        ...DELEGATION_EDIT_ACTION,
        label: this.transloco.translate(DELEGATION_EDIT_ACTION.label),
      },
    ];
  });

  private get loggedInUserId(): string {
    const authData = JSON.parse(
      this.cookieService.get(AppStorage.centralUser) || '{}'
    ) as { id?: number | string };

    return String(authData.id ?? '');
  }

  ngOnInit(): void {
    this.route.params
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params => {
        const delegationId = params['id'];
        if (!delegationId) {
          this.goBack();
          return;
        }
        this.loadDetails(delegationId);
      });
  }

  goBack(): void {
    history.back();
  }

  onActionClick(actionKey: string): void {
    if (actionKey !== DELEGATION_EDIT_ACTION.key) {
      return;
    }

    const delegationId = this.details()?.id;
    if (!delegationId) {
      return;
    }

    this.router.navigate([
      this.tenantService.tenantPath(
        `${EMPLOYEE_ROUTES.ACCOUNT_SETTINGS.DELEGATION_FORM}/${delegationId}`
      ),
    ]);
  }

  private loadDetails(delegationId: string): void {
    this.isLoading.set(true);
    this.getDelegationDetails$(delegationId)
      .pipe(
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: res => this.details.set(res.data),
        error: () => this.goBack(),
      });
  }

  getDelegationDetails$(id: string) {
    return this.delegationService.getDelegation(id);
  }
}
